import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import {
  MapCustomMapCollection,
  MapCustomMapStarterSheetBody,
  MapCustomMapsEmptyState,
  MapCustomMapsLibraryPage,
  MapCustomMapsToolbar,
  type MapCustomMapLibraryCardItem,
  type MapCustomMapsEmptyStateAction,
  type MapCustomMapsToolbarChip
} from '@workspace/silk-ui';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@workspace/ui';

import { MappingAssetAccessSheet } from '@/components/mapping/MappingAssetAccessSheet';
import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import { useNavigation } from '@/context/NavigationContext';
import {
  buildCompositionMetadata,
  buildOverlayStyleFromRuntime,
  formatShareScopeLabel,
  formatViewportSummary,
  getCustomMapConfig,
  isCustomMapAsset,
  isPortalAssetReadOnly,
  normalizeShareScope,
} from '@/lib/mapping-assets';
import { useMappingAccess } from '@/lib/useMappingAccess';
import {
  createMapStyle,
  deleteMapStyle,
  listMapSources,
  listMapStyleShares,
  listMapStyles,
  replaceMapStyleShares,
  updateMapStyle,
  type MapSource,
  type MapStyle,
  type MapStyleCreateRequest,
  type ShareScope
} from '@/lib/mapping-api';

const DEFAULT_PAGE_SIZE = 200;

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const countSources = (style?: MapStyle | null) => {
  if (!style) return 0;
  const metadataSources = Array.isArray(style.metadata?.source_refs) ? style.metadata?.source_refs : null;
  if (metadataSources) {
    return metadataSources.length;
  }
  const sources = style.style_json && typeof style.style_json === 'object' ? (style.style_json as { sources?: Record<string, unknown> }).sources : null;
  if (sources && typeof sources === 'object') {
    return Object.keys(sources).length;
  }
  return 0;
};

const countLayers = (style?: MapStyle | null) => {
  const overlayStyle = buildOverlayStyleFromRuntime(style?.style_json ?? null, style?.metadata ?? null);
  const layers = overlayStyle?.layers;
  return Array.isArray(layers) ? layers.length : 0;
};

const cloneStyleJson = (styleJson?: Record<string, unknown> | null) => {
  if (!styleJson) {
    return {};
  }
  if (typeof structuredClone === 'function') {
    return structuredClone(styleJson);
  }
  return JSON.parse(JSON.stringify(styleJson)) as Record<string, unknown>;
};

const resolvePresetStyleJson = (style?: MapStyle | null) =>
  buildOverlayStyleFromRuntime(style?.style_json ?? null, style?.metadata ?? null);

const buildSourceSummary = (style?: MapStyle | null) => {
  const refs = Array.isArray(style?.metadata?.source_refs) ? style.metadata.source_refs : [];
  if (refs.length === 0) {
    return 'No published sources';
  }
  const labels = refs.map(entry => entry?.name ?? entry?.id).filter(Boolean);
  if (labels.length === 0) {
    return `${refs.length} included ${refs.length === 1 ? 'source' : 'sources'}`;
  }
  if (labels.length === 1) {
    return labels[0] as string;
  }
  if (labels.length === 2) {
    return `${labels[0]} + ${labels[1]}`;
  }
  return `${labels[0]} + ${labels.length - 1} more sources`;
};

export default function MapCustomMapsPage() {
  const { navigate } = useNavigation();
  const mappingAccess = useMappingAccess();
  const [shareScopeFilter, setShareScopeFilter] = useState<'all' | ShareScope>('all');
  const [search, setSearch] = useState('');
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessTarget, setAccessTarget] = useState<MapStyle | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [starterOpen, setStarterOpen] = useState(false);
  const [starterStyleId, setStarterStyleId] = useState('');
  const [starterName, setStarterName] = useState('');
  const [starterError, setStarterError] = useState<string | null>(null);
  const [creatingStarter, setCreatingStarter] = useState(false);

  const { data, error, isLoading, mutate } = useSWR<MapStyle[]>(
    mappingAccess.accessEnabled ? ['custom-maps', shareScopeFilter] : null,
    async () => {
      const items = await listMapStyles({
        limit: DEFAULT_PAGE_SIZE,
        share_scope: shareScopeFilter === 'all' ? undefined : shareScopeFilter,
      });
      return items.filter(isCustomMapAsset);
    }
  );

  const { data: sourceCatalog = [] } = useSWR<MapSource[]>(
    mappingAccess.accessEnabled ? ['map-sources-for-custom-maps'] : null,
    () => listMapSources({ limit: DEFAULT_PAGE_SIZE }),
  );

  const { data: stylePresets = [] } = useSWR<MapStyle[]>(
    mappingAccess.accessEnabled ? ['map-style-presets-for-custom-maps'] : null,
    async () => {
      const items = await listMapStyles({ limit: DEFAULT_PAGE_SIZE });
      return items.filter(style => !isCustomMapAsset(style));
    },
  );

  const filteredMaps = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(entry =>
      [entry.name, entry.description, entry.id]
        .filter(Boolean)
        .some(value => value!.toLowerCase().includes(needle))
    );
  }, [data, search]);

  const publishedStyleOptions = useMemo(
    () =>
      [...stylePresets].sort((left, right) => (left.name ?? left.id ?? '').localeCompare(right.name ?? right.id ?? '')),
    [stylePresets],
  );

  const selectedStylePreset = useMemo(
    () => publishedStyleOptions.find(style => style.id === starterStyleId) ?? null,
    [publishedStyleOptions, starterStyleId],
  );

  const toolbarChips = useMemo<MapCustomMapsToolbarChip[]>(
    () => {
      const chips: MapCustomMapsToolbarChip[] = [];
      if (shareScopeFilter !== 'all') {
        chips.push({
          id: 'scope-chip',
          label: `Scope: ${formatShareScopeLabel(shareScopeFilter)}`,
          tone: 'muted',
        });
      }
      if (search.trim()) {
        chips.push({
          id: 'search-chip',
          label: `Searching: ${search.trim()}`,
          tone: 'muted',
        });
      }
      return chips;
    },
    [search, shareScopeFilter],
  );

  const starterSummaryItems = useMemo(
    () =>
      selectedStylePreset
        ? [
            {
              id: 'starter-scope',
              label: 'Share Scope',
              value: normalizeShareScope(selectedStylePreset.share_scope),
            },
            {
              id: 'starter-style',
              label: 'Style Preset',
              value: selectedStylePreset.name ?? selectedStylePreset.id ?? 'Selected style',
            },
            {
              id: 'starter-source-count',
              label: 'Included Sources',
              value: String(countSources(selectedStylePreset)),
            },
            {
              id: 'starter-layer-count',
              label: 'Authored Layers',
              value: String(countLayers(selectedStylePreset)),
            },
          ]
        : [],
    [selectedStylePreset],
  );

  const collectionItems = useMemo<MapCustomMapLibraryCardItem[]>(
    () =>
      filteredMaps.map(style => {
        const customMap = getCustomMapConfig(style);
        const readOnly = isPortalAssetReadOnly(style.tenant_id, mappingAccess.featureTenantId);
        const scopeLabel = formatShareScopeLabel(style.share_scope);
        return {
          id: style.id ?? style.name ?? 'custom-map',
          title: style.name ?? 'Untitled custom map',
          description:
            style.description ?? 'Prepared map experience published into the current tenant context for reuse and customization.',
          eyebrow: 'Prepared Map',
          scopeLabel,
          ownerLabel: readOnly ? 'Shared with current tenant' : 'Owned by current tenant',
          updatedLabel: `Updated ${formatDate(style.updated_at)}`,
          mapIdLabel: style.id ?? null,
          sourceSummary: buildSourceSummary(style),
          viewportSummary: formatViewportSummary(customMap?.initial_view ?? null),
          featured: normalizeShareScope(style.share_scope) === 'public',
          thumbnailLabel: style.name?.slice(0, 2).toUpperCase() ?? 'CM',
          statusBadges: [
            { id: `kind-${style.id}`, label: 'Custom map', tone: 'muted' },
            { id: `scope-${style.id}`, label: scopeLabel, tone: 'default' },
            ...(readOnly ? [{ id: `readonly-${style.id}`, label: 'Read-only', tone: 'warning' as const }] : []),
          ],
          stats: [
            {
              id: `sources-${style.id}`,
              label: 'Sources',
              value: String(countSources(style)),
            },
            {
              id: `rules-${style.id}`,
              label: 'Rules',
              value: String(customMap?.preset_summary?.rule_count ?? 0),
            },
            {
              id: `layers-${style.id}`,
              label: 'Layers',
              value: String(customMap?.preset_summary?.layer_count ?? 0),
            },
          ],
          primaryAction: {
            id: 'open',
            label: readOnly ? 'Open Map' : 'Open Workspace',
          },
          secondaryActions: [
            { id: 'access', label: 'Access' },
            { id: 'duplicate', label: 'Duplicate' },
            ...(readOnly ? [] : [{ id: 'delete', label: 'Delete', tone: 'danger' as const }]),
          ],
        } satisfies MapCustomMapLibraryCardItem;
      }),
    [filteredMaps, mappingAccess.featureTenantId],
  );

  useEffect(() => {
    if (!starterStyleId) return;
    if (!publishedStyleOptions.some(style => style.id === starterStyleId)) {
      setStarterStyleId('');
    }
  }, [publishedStyleOptions, starterStyleId]);

  useEffect(() => {
    if (!selectedStylePreset || starterName.trim()) return;
    setStarterName(`${selectedStylePreset.name ?? 'Map'} Map`);
  }, [selectedStylePreset, starterName]);

  const handleDelete = async (style: MapStyle) => {
    if (!style.id) return;
    if (!window.confirm(`Delete custom map "${style.name ?? style.id}"?`)) {
      return;
    }
    try {
      await deleteMapStyle(style.id);
      await mutate();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleDuplicate = async (style: MapStyle) => {
    if (!style.style_json) {
      return;
    }
    const payload: MapStyleCreateRequest = {
      name: `Copy of ${style.name ?? 'Custom Map'}`,
      share_scope: normalizeShareScope(style.share_scope),
      description: style.description ?? undefined,
      style_json: style.style_json,
      metadata: style.metadata ?? undefined,
    };
    try {
      const created = await createMapStyle(payload);
      await mutate();
      if (created?.id) {
        navigate(`/maps/custom-maps/${created.id}/workbench`);
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Duplicate failed');
    }
  };

  const handleCreateFromPreset = async () => {
    setStarterError(null);
    const presetStyleJson = resolvePresetStyleJson(selectedStylePreset);
    if (!starterStyleId || !presetStyleJson) {
      setStarterError('Choose a published style preset.');
      return;
    }
    const name = starterName.trim();
    if (!name) {
      setStarterError('Map name is required.');
      return;
    }

    setCreatingStarter(true);
    try {
      const styleJson = cloneStyleJson(presetStyleJson);
      const metadata = buildCompositionMetadata({
        existingMetadata: selectedStylePreset.metadata ?? null,
        styleJson,
        authoredStyleJson: styleJson,
        sources: sourceCatalog,
        mapExperience:
          selectedStylePreset.metadata?.map_experience &&
          typeof selectedStylePreset.metadata.map_experience === 'object'
            ? (selectedStylePreset.metadata.map_experience as Record<string, unknown>)
            : null,
        customMap: true,
        initialView:
          selectedStylePreset.metadata?.custom_map && typeof selectedStylePreset.metadata.custom_map === 'object'
            ? (selectedStylePreset.metadata.custom_map as { initial_view?: { center?: [number, number]; zoom?: number; bearing?: number; pitch?: number } | null }).initial_view ?? null
            : null,
      });

      const created = await createMapStyle({
        name,
        description:
          selectedStylePreset.description ??
          `Built from the ${selectedStylePreset.name ?? 'selected'} published preset.`,
        share_scope: normalizeShareScope(selectedStylePreset.share_scope),
        style_json: styleJson,
        metadata,
      });

      await mutate();
      setCreatingStarter(false);
      setStarterOpen(false);
      setStarterStyleId('');
      setStarterName('');
      if (created?.id) {
        navigate(`/maps/custom-maps/${created.id}/workbench?mode=author`);
      }
    } catch (err) {
      setStarterError(err instanceof Error ? err.message : 'Create custom map failed');
      setCreatingStarter(false);
    }
  };

  const handleCollectionAction = (mapId: string, actionId: string) => {
    const style = filteredMaps.find(entry => entry.id === mapId) ?? data?.find(entry => entry.id === mapId) ?? null;
    if (!style?.id) {
      return;
    }

    switch (actionId) {
      case 'open':
        navigate(`/maps/custom-maps/${style.id}/workbench`);
        return;
      case 'access':
        setAccessTarget(style);
        setAccessOpen(true);
        return;
      case 'duplicate':
        void handleDuplicate(style);
        return;
      case 'delete':
        void handleDelete(style);
        return;
      default:
        return;
    }
  };

  if (!mappingAccess.mappingFeatureEnabled) {
    return <MappingFeatureUnavailable />;
  }

  if (!mappingAccess.mappingPermissionGranted) {
    return <MappingPermissionDenied />;
  }

  const hasActiveLibraryFilters = shareScopeFilter !== 'all' || search.trim().length > 0;

  const emptyState = (() => {
    const actions: MapCustomMapsEmptyStateAction[] = [];
    if (error) {
      actions.push({ id: 'retry', label: 'Retry' });
      return (
        <MapCustomMapsEmptyState
          title="Unable to load tenant custom maps"
          description={error.message}
          helperText="The page could not refresh the library. Retry the request or reopen the page after backend connectivity is restored."
          actions={actions}
          onAction={actionId => {
            if (actionId === 'retry') {
              void mutate();
            }
          }}
        />
      );
    }

    if (hasActiveLibraryFilters) {
      actions.push({ id: 'reset', label: 'Clear filters' });
      return (
        <MapCustomMapsEmptyState
          title="No maps match the current tenant library view"
          description="Adjust the search term or reset the scope filter to return to the broader custom-map catalog."
          actions={actions}
          onAction={actionId => {
            if (actionId === 'reset') {
              setSearch('');
              setShareScopeFilter('all');
            }
          }}
        />
      );
    }

    actions.push({ id: 'create', label: 'Create from preset' });
    actions.push({ id: 'styles', label: 'Browse styles', tone: 'muted' });
    return (
      <MapCustomMapsEmptyState
        title="No custom maps in this tenant context yet"
        description="Create a prepared map for the active tenant and reopen it through the same shared custom-map library."
        helperText="Shared and public maps will also appear here once they are published into a scope the active tenant can access."
        actions={actions}
        onAction={actionId => {
          if (actionId === 'create') {
            setStarterOpen(true);
            return;
          }
          if (actionId === 'styles') {
            navigate('/maps/styles');
          }
        }}
      />
    );
  })();

  return (
    <div className="space-y-6">
      {errorMessage ? (
        <MapCustomMapsEmptyState
          title="A custom-map action did not complete"
          description={errorMessage}
          helperText="Retry the action or refresh the tenant library once the backend state settles."
          actions={[{ id: 'dismiss', label: 'Dismiss' }]}
          onAction={() => setErrorMessage(null)}
        />
      ) : null}

      <MapCustomMapsLibraryPage
        toolbar={
          <MapCustomMapsToolbar
            searchValue={search}
            searchPlaceholder="Search custom maps"
            scopeValue={shareScopeFilter}
            scopeOptions={[
              { value: 'all', label: 'All scopes' },
              { value: 'public', label: 'Public' },
              { value: 'shared', label: 'Shared' },
              { value: 'tenant', label: 'Tenant' },
            ]}
            summaryLabel={`Showing ${filteredMaps.length} of ${data?.length ?? 0} prepared maps`}
            chips={toolbarChips}
            secondaryActionLabel="Reset"
            primaryActionLabel="New Map"
            onSearchChange={setSearch}
            onScopeChange={value => setShareScopeFilter(value as 'all' | ShareScope)}
            onSecondaryAction={() => {
              setSearch('');
              setShareScopeFilter('all');
            }}
            onPrimaryAction={() => setStarterOpen(true)}
          />
        }
        content={
          <MapCustomMapCollection
            items={collectionItems}
            loading={isLoading}
            emptyState={emptyState}
            onAction={handleCollectionAction}
          />
        }
      />

      <Sheet
        open={starterOpen}
        onOpenChange={open => {
          setStarterOpen(open);
          if (!open) {
            setStarterError(null);
            setStarterStyleId('');
            setStarterName('');
            setCreatingStarter(false);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>New Map</SheetTitle>
            <SheetDescription>
              Start from a published style preset that already includes the right data sources and authored layer
              logic. Use the styles library only when you need to inspect or author presets directly.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            <MapCustomMapStarterSheetBody
              title="Create Custom Map"
              description="Name the map, then choose the published style preset you want to start from. Each preset can include one or many sources."
              mapName={starterName}
              mapNamePlaceholder="Regional network map"
              styleValue={starterStyleId}
              styleOptions={publishedStyleOptions.map(style => ({
                value: style.id ?? '',
                label: style.name ?? style.id ?? 'Untitled style',
                description: style.description ?? null,
                metaLabel: [
                  normalizeShareScope(style.share_scope),
                  `${countSources(style)} ${countSources(style) === 1 ? 'source' : 'sources'}`,
                  `${countLayers(style)} ${countLayers(style) === 1 ? 'layer' : 'layers'}`,
                ].join(' • '),
                sourceSummary: buildSourceSummary(style),
              }))}
              stylePlaceholder="Choose a published style preset"
              styleEmptyMessage="No published presets are available yet."
              summaryItems={starterSummaryItems}
              helperText={
                publishedStyleOptions.length === 0
                  ? 'No published presets are available yet. Open the Styles library to author one.'
                  : selectedStylePreset
                    ? `${selectedStylePreset.description ?? 'This preset is ready to create a custom map.'} Included sources: ${buildSourceSummary(selectedStylePreset)}.`
                    : 'Choose a preset to see its included sources and authored layer coverage.'
              }
              errorMessage={starterError}
              submitting={creatingStarter}
              onMapNameChange={setStarterName}
              onStyleChange={setStarterStyleId}
              onCancel={() => setStarterOpen(false)}
              onSubmit={handleCreateFromPreset}
              secondaryActionLabel="Browse styles"
              onSecondaryAction={() => navigate('/maps/styles')}
            />
          </div>
        </SheetContent>
      </Sheet>

      <MappingAssetAccessSheet
        open={accessOpen}
        onOpenChange={setAccessOpen}
        assetTypeLabel="Custom map"
        asset={accessTarget}
        featureTenantId={mappingAccess.featureTenantId}
        loadShares={assetId => listMapStyleShares(assetId)}
        saveAccess={async (asset, input) => {
          if (!asset.id) {
            throw new Error('Custom map ID required');
          }
          await updateMapStyle(asset.id, { share_scope: input.shareScope });
          await replaceMapStyleShares(asset.id, input.tenantIds);
          await mutate();
        }}
      />
    </div>
  );
}
